import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AdultModelApplicationRecord,
  AdultModelApplicationStatus,
  ChildModelApplicationRecord,
  ChildModelApplicationStatus,
  ModelApplicationNotificationSettings,
} from '../types';
import { useSiteAuth } from '../hooks/useSiteAuth';
import {
  emailModelApplicationSummary,
  getModelApplicationNotificationRecipient,
  listAdultModelApplications,
  updateModelApplicationNotificationRecipient,
  updateAdultModelApplicationStatus,
} from '../services/adult-model-applications';
import {
  listChildModelApplications,
  updateChildModelApplicationStatus,
} from '../services/child-model-applications';

type ModelApplicationStatus =
  | AdultModelApplicationStatus
  | ChildModelApplicationStatus;

type ModelTypeFilter = 'all' | 'adult' | 'child';

type ReviewableModelApplication = {
  id: string;
  modelType: 'adult' | 'child';
  eventTitle: string;
  ticketName: string;
  status: ModelApplicationStatus;
  submittedAt: number;
  reviewedAt: number | null;
  reviewNotes: string;
  applicationMessage: string;
  availabilityDetails: string;
  primaryName: string;
  primaryEmail: string;
  primaryPhone: string;
  birthday: string;
  city: string;
  state: string;
  cityState: string;
  instagramHandle: string;
  experienceSummary: string;
  profileHeight: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  guardianRelationship: string;
  childFirstName: string;
  childLastName: string;
  childName: string;
  childAge: string;
  allergiesOrAccommodations: string;
  participationConsent: boolean;
  mediaReleaseApproved: boolean;
};

const statusOptions: Array<{
  value: ModelApplicationStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All applications' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'declined', label: 'Declined' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const modelTypeOptions: Array<{
  value: ModelTypeFilter;
  label: string;
}> = [
  { value: 'all', label: 'All model types' },
  { value: 'adult', label: 'Adult models' },
  { value: 'child', label: 'Child models' },
];

function formatDate(timestamp: number | null): string {
  if (!timestamp) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp * 1000);
}

function formatStatusLabel(value: string): string {
  return value.replace(/-/g, ' ');
}

function normalizeAdultApplication(application: AdultModelApplicationRecord): ReviewableModelApplication {
  return {
    id: application.id,
    modelType: 'adult',
    eventTitle: application.event_title,
    ticketName: application.ticket_name,
    status: application.status,
    submittedAt: application.submitted_at,
    reviewedAt: application.reviewed_at,
    reviewNotes: application.review_notes,
    applicationMessage: application.application_message,
    availabilityDetails: application.availability_details,
    primaryName: application.profile_display_name || application.applicant_name,
    primaryEmail: application.applicant_email,
    primaryPhone: application.applicant_phone,
    birthday: application.applicant_birthday,
    city: application.applicant_city,
    state: application.applicant_state,
    cityState: application.profile_city_state,
    instagramHandle: application.profile_instagram_handle,
    experienceSummary: application.profile_experience_summary,
    profileHeight: application.profile_height,
    guardianName: '',
    guardianEmail: '',
    guardianPhone: '',
    guardianRelationship: '',
    childFirstName: '',
    childLastName: '',
    childName: '',
    childAge: '',
    allergiesOrAccommodations: '',
    participationConsent: false,
    mediaReleaseApproved: false,
  };
}

function normalizeChildApplication(application: ChildModelApplicationRecord): ReviewableModelApplication {
  return {
    id: application.id,
    modelType: 'child',
    eventTitle: application.event_title,
    ticketName: application.ticket_name,
    status: application.status,
    submittedAt: application.submitted_at,
    reviewedAt: application.reviewed_at,
    reviewNotes: application.review_notes,
    applicationMessage: application.application_message,
    availabilityDetails: application.availability_details,
    primaryName: application.child_name,
    primaryEmail: application.guardian_email,
    primaryPhone: application.guardian_phone,
    birthday: application.child_birthday,
    city: application.child_city,
    state: application.child_state,
    cityState: application.child_city_state,
    instagramHandle: application.child_instagram_handle,
    experienceSummary: application.child_experience_summary,
    profileHeight: '',
    guardianName: application.guardian_name,
    guardianEmail: application.guardian_email,
    guardianPhone: application.guardian_phone,
    guardianRelationship: application.guardian_relationship,
    childFirstName: application.child_first_name,
    childLastName: application.child_last_name,
    childName: application.child_name,
    childAge: application.child_age,
    allergiesOrAccommodations: application.allergies_or_accommodations,
    participationConsent: application.participation_consent,
    mediaReleaseApproved: application.media_release_approved,
  };
}

export default function PortalModelApplications() {
  const { session } = useSiteAuth();
  const [applications, setApplications] = useState<ReviewableModelApplication[]>([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ModelApplicationStatus | 'all'>('all');
  const [modelTypeFilter, setModelTypeFilter] = useState<ModelTypeFilter>('all');
  const [reviewNotes, setReviewNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summaryRecipientEmail, setSummaryRecipientEmail] = useState('');
  const [isSendingSummaryEmail, setIsSendingSummaryEmail] = useState(false);
  const [defaultRecipientEmail, setDefaultRecipientEmail] = useState('');
  const [isSavingDefaultRecipient, setIsSavingDefaultRecipient] = useState(false);
  const [savedNotificationSettings, setSavedNotificationSettings] = useState<ModelApplicationNotificationSettings | null>(null);

  const canManageApplications = session?.role === 'admin';

  const loadApplications = useCallback(async (mode: 'initial' | 'refresh') => {
    if (!canManageApplications) {
      setApplications([]);
      setSelectedApplicationId(null);
      setIsLoading(false);
      return;
    }

    const setLoadingState = mode === 'initial' ? setIsLoading : setIsRefreshing;

    try {
      setLoadingState(true);
      setError(null);

      const shouldLoadAdult = modelTypeFilter === 'all' || modelTypeFilter === 'adult';
      const shouldLoadChild = modelTypeFilter === 'all' || modelTypeFilter === 'child';

      const [adultApplications, childApplications] = await Promise.all([
        shouldLoadAdult ? listAdultModelApplications({ status: statusFilter, limit: 150 }) : Promise.resolve([]),
        shouldLoadChild ? listChildModelApplications({ status: statusFilter, limit: 150 }) : Promise.resolve([]),
      ]);

      const nextApplications = [
        ...adultApplications.map(normalizeAdultApplication),
        ...childApplications.map(normalizeChildApplication),
      ].sort((left, right) => right.submittedAt - left.submittedAt);

      setApplications(nextApplications);
      setSelectedApplicationId((current) => {
        if (current && nextApplications.some((application) => application.id === current)) {
          return current;
        }

        return nextApplications[0]?.id ?? null;
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to load model applications right now.');
      setApplications([]);
      setSelectedApplicationId(null);
    } finally {
      setLoadingState(false);
    }
  }, [canManageApplications, modelTypeFilter, statusFilter]);

  useEffect(() => {
    void loadApplications('initial');
  }, [loadApplications]);

  const selectedApplication = useMemo(
    () => applications.find((application) => application.id === selectedApplicationId) ?? null,
    [applications, selectedApplicationId],
  );

  useEffect(() => {
    setReviewNotes(selectedApplication?.reviewNotes || '');
  }, [selectedApplication?.id, selectedApplication?.reviewNotes]);

  useEffect(() => {
    if (!summaryRecipientEmail && session?.email) {
      setSummaryRecipientEmail(session.email);
    }
  }, [session?.email, summaryRecipientEmail]);

  useEffect(() => {
    if (!canManageApplications) {
      setSavedNotificationSettings(null);
      setDefaultRecipientEmail('');
      return;
    }

    let isMounted = true;

    void (async () => {
      try {
        const settings = await getModelApplicationNotificationRecipient();
        if (!isMounted) {
          return;
        }

        setSavedNotificationSettings(settings);
        setDefaultRecipientEmail(settings.recipient_email || '');
        if (!summaryRecipientEmail && settings.recipient_email) {
          setSummaryRecipientEmail(settings.recipient_email);
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setSavedNotificationSettings(null);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [canManageApplications, summaryRecipientEmail]);

  async function refreshApplications() {
    await loadApplications('refresh');
  }

  async function handleUpdateStatus(nextStatus: ModelApplicationStatus) {
    if (!selectedApplication || !canManageApplications || selectedApplication.status === nextStatus) {
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      const updated =
        selectedApplication.modelType === 'adult'
          ? normalizeAdultApplication(
              await updateAdultModelApplicationStatus(selectedApplication.id, {
                status: nextStatus as AdultModelApplicationStatus,
                reviewNotes,
              }),
            )
          : normalizeChildApplication(
              await updateChildModelApplicationStatus(selectedApplication.id, {
                status: nextStatus as ChildModelApplicationStatus,
                reviewNotes,
              }),
            );

      setApplications((current) =>
        current
          .map((application) => (application.id === updated.id ? updated : application))
          .sort((left, right) => right.submittedAt - left.submittedAt),
      );
      setSelectedApplicationId(updated.id);
      setFeedback(
        `${updated.modelType === 'adult' ? 'Adult' : 'Child'} model application updated to ${formatStatusLabel(updated.status)}.`,
      );
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to update this model application right now.');
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleSendSummaryEmail() {
    if (!summaryRecipientEmail.trim()) {
      setError('Enter an email address before sending the model summary.');
      return;
    }

    try {
      setIsSendingSummaryEmail(true);
      setError(null);
      setFeedback(null);

      const result = await emailModelApplicationSummary({
        recipientEmail: summaryRecipientEmail.trim(),
        status: statusFilter,
        modelType: modelTypeFilter,
      });

      setFeedback(`Sent ${result.count} model applications to ${result.recipient_email}.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to email the model list right now.');
    } finally {
      setIsSendingSummaryEmail(false);
    }
  }

  async function handleSaveDefaultRecipient() {
    try {
      setIsSavingDefaultRecipient(true);
      setError(null);
      setFeedback(null);

      const settings = await updateModelApplicationNotificationRecipient({
        recipientEmail: defaultRecipientEmail,
      });

      setSavedNotificationSettings(settings);
      setDefaultRecipientEmail(settings.recipient_email || '');
      if (settings.recipient_email) {
        setSummaryRecipientEmail(settings.recipient_email);
        setFeedback(`New registrations will now email ${settings.recipient_email} automatically.`);
      } else {
        setFeedback('Automatic model registration emails are now turned off.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the default recipient right now.');
    } finally {
      setIsSavingDefaultRecipient(false);
    }
  }

  if (!session) {
    return null;
  }

  if (!canManageApplications) {
    return (
      <div className="portal-guard-state">
        <p className="info-kicker">Model applications</p>
        <h1>Model review is limited to portal admins.</h1>
        <p>Contact Victoria&apos;s Place leadership if you need application review access.</p>
      </div>
    );
  }

  const submittedCount = applications.filter((application) => application.status === 'submitted').length;
  const approvedCount = applications.filter((application) => application.status === 'approved').length;
  const waitlistedCount = applications.filter((application) => application.status === 'waitlisted').length;

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className="portal-section portal-section--feature">
        <div className="portal-section-heading">
          <div>
            <p className="info-kicker">Model applications</p>
            <h1>Review adult and child model registrations from one shared event queue.</h1>
          </div>

          <div className="portal-inline-actions">
            <button
              type="button"
              className="button-secondary tap-target"
              onClick={() => void refreshApplications()}
              disabled={isRefreshing || isLoading}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh applications'}
            </button>
          </div>
        </div>

        <p className="portal-feature-copy">
          Adult and child model applications can now be submitted directly from the public event page without account
          creation. Both flow into the same admin review workspace here.
        </p>

        {feedback ? <div className="auth-success">{feedback}</div> : null}
        {error ? <div className="auth-runtime-note"><p>{error}</p></div> : null}

        <div className="portal-runtime-list">
          <div className="portal-runtime-row">
            <span>Total applications</span>
            <strong>{isLoading ? '...' : applications.length}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Submitted</span>
            <strong>{isLoading ? '...' : submittedCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Approved</span>
            <strong>{isLoading ? '...' : approvedCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Waitlisted</span>
            <strong>{isLoading ? '...' : waitlistedCount}</strong>
          </div>
        </div>

        <div className="portal-form-grid portal-form-grid--orders">
          <label className="auth-field portal-field-span-full">
            <span>Default email for new registrations</span>
            <input
              type="email"
              className="auth-input"
              value={defaultRecipientEmail}
              onChange={(event) => setDefaultRecipientEmail(event.target.value)}
              placeholder="siteowner@example.com"
            />
          </label>
          <label className="auth-field portal-field-span-full">
            <span>Email the current model list</span>
            <input
              type="email"
              className="auth-input"
              value={summaryRecipientEmail}
              onChange={(event) => setSummaryRecipientEmail(event.target.value)}
              placeholder="siteowner@example.com"
            />
          </label>
        </div>

        <div className="portal-inline-actions">
          <button
            type="button"
            className="button-secondary tap-target"
            onClick={() => void handleSaveDefaultRecipient()}
            disabled={isSavingDefaultRecipient}
          >
            {isSavingDefaultRecipient ? 'Saving default email...' : 'Save default email'}
          </button>
          <button
            type="button"
            className="button-primary tap-target"
            onClick={() => void handleSendSummaryEmail()}
            disabled={isSendingSummaryEmail || isLoading || !applications.length}
          >
            {isSendingSummaryEmail ? 'Sending model list...' : 'Send model list email'}
          </button>
        </div>

        {savedNotificationSettings?.recipient_email ? (
          <p className="portal-feature-copy">
            New model registrations are currently emailed automatically to {savedNotificationSettings.recipient_email}.
          </p>
        ) : (
          <p className="portal-feature-copy">
            Save a default email above if each new model registration should send automatically as it happens.
          </p>
        )}
      </section>

      <div className="portal-orders-grid">
        <section className="portal-section portal-orders-list-panel">
          <div className="portal-section-heading">
            <div>
              <p className="info-kicker">Filters</p>
              <h2>Current applications</h2>
            </div>
          </div>

          <div className="portal-form-grid portal-form-grid--orders">
            <label className="auth-field">
              <span>Model type</span>
              <select
                className="auth-input"
                value={modelTypeFilter}
                onChange={(event) => setModelTypeFilter(event.target.value as ModelTypeFilter)}
              >
                {modelTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-field">
              <span>Application status</span>
              <select
                className="auth-input"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as ModelApplicationStatus | 'all')}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="portal-orders-list">
            {isLoading ? (
              <div className="auth-runtime-note">
                <p>Loading saved model applications...</p>
              </div>
            ) : applications.length ? (
              applications.map((application) => (
                <button
                  key={application.id}
                  type="button"
                  onClick={() => setSelectedApplicationId(application.id)}
                  className={`portal-order-card ${selectedApplicationId === application.id ? 'portal-order-card--active' : ''}`}
                >
                  <div className="portal-order-card-row">
                    <strong>{application.primaryName}</strong>
                    <span className={`portal-model-status-badge portal-model-status-badge--${application.status}`}>
                      {formatStatusLabel(application.status)}
                    </span>
                  </div>
                  <p>{application.eventTitle}</p>
                  <div className="portal-order-card-meta">
                    <span>{application.modelType === 'adult' ? 'Adult model' : 'Child model'}</span>
                    <span>{application.ticketName}</span>
                  </div>
                  <div className="portal-order-card-meta">
                    <span>{application.cityState || 'Location pending'}</span>
                    <span>{formatDate(application.submittedAt)}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="auth-runtime-note">
                <p>No model applications match the current filters yet.</p>
              </div>
            )}
          </div>
        </section>

        <section className="portal-section portal-order-detail-panel">
          {selectedApplication ? (
            <>
              <div className="portal-section-heading">
                <div>
                  <p className="info-kicker">Application detail</p>
                  <h2>{selectedApplication.primaryName}</h2>
                </div>

                <div className="portal-inline-actions">
                  <span className={`portal-model-status-badge portal-model-status-badge--${selectedApplication.status}`}>
                    {formatStatusLabel(selectedApplication.status)}
                  </span>
                </div>
              </div>

              <div className="portal-order-detail-grid">
                <article className="portal-account-card">
                  <p className="info-kicker">{selectedApplication.modelType === 'adult' ? 'Applicant' : 'Child model'}</p>
                  <h3>{selectedApplication.primaryName}</h3>
                  <p className="portal-detail-copy">{selectedApplication.cityState || 'Location pending'}</p>
                </article>

                <article className="portal-account-card">
                  <p className="info-kicker">Event</p>
                  <h3>{selectedApplication.eventTitle}</h3>
                  <p className="portal-detail-copy">{selectedApplication.ticketName}</p>
                </article>
              </div>

              {selectedApplication.modelType === 'child' ? (
                <div className="portal-order-detail-grid">
                  <article className="portal-account-card">
                    <p className="info-kicker">Parent or guardian</p>
                    <h3>{selectedApplication.guardianName}</h3>
                    <p className="portal-detail-copy">{selectedApplication.guardianEmail}</p>
                  </article>

                  <article className="portal-account-card">
                    <p className="info-kicker">Guardian contact</p>
                    <h3>{selectedApplication.guardianPhone || 'Not provided'}</h3>
                    <p className="portal-detail-copy">{selectedApplication.guardianRelationship || 'Relationship pending'}</p>
                  </article>
                </div>
              ) : null}

              <div className="portal-runtime-list">
                <div className="portal-runtime-row">
                  <span>Application ID</span>
                  <strong>{selectedApplication.id}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Model type</span>
                  <strong>{selectedApplication.modelType === 'adult' ? 'Adult model' : 'Child model'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Submitted</span>
                  <strong>{formatDate(selectedApplication.submittedAt)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Reviewed</span>
                  <strong>{formatDate(selectedApplication.reviewedAt)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Email</span>
                  <strong>{selectedApplication.primaryEmail || 'Not provided'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Phone</span>
                  <strong>{selectedApplication.primaryPhone || 'Not provided'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Birthday</span>
                  <strong>{selectedApplication.birthday || 'Not provided'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>City</span>
                  <strong>{selectedApplication.city || 'Not provided'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>State</span>
                  <strong>{selectedApplication.state || 'Not provided'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>City and state</span>
                  <strong>{selectedApplication.cityState || 'Not provided'}</strong>
                </div>
                {selectedApplication.modelType === 'adult' ? (
                  <div className="portal-runtime-row">
                    <span>Height</span>
                    <strong>{selectedApplication.profileHeight || 'Not provided'}</strong>
                  </div>
                ) : (
                  <>
                    <div className="portal-runtime-row">
                      <span>Child first name</span>
                      <strong>{selectedApplication.childFirstName || 'Not provided'}</strong>
                    </div>
                    <div className="portal-runtime-row">
                      <span>Child last name</span>
                      <strong>{selectedApplication.childLastName || 'Not provided'}</strong>
                    </div>
                    <div className="portal-runtime-row">
                      <span>Child age</span>
                      <strong>{selectedApplication.childAge || 'Not provided'}</strong>
                    </div>
                  </>
                )}
                <div className="portal-runtime-row">
                  <span>Instagram</span>
                  <strong>{selectedApplication.instagramHandle || 'Not provided'}</strong>
                </div>
              </div>

              <div className="portal-meta-grid">
                <section className="portal-section">
                  <p className="info-kicker">Experience</p>
                  <p className="portal-detail-copy">
                    {selectedApplication.experienceSummary || 'No experience summary was provided yet.'}
                  </p>
                </section>

                <section className="portal-section">
                  <p className="info-kicker">Application message</p>
                  <p className="portal-detail-copy">
                    {selectedApplication.applicationMessage || 'No application message was provided yet.'}
                  </p>
                </section>
              </div>

              <section className="portal-section">
                <p className="info-kicker">Availability details</p>
                <p className="portal-detail-copy">
                  {selectedApplication.availabilityDetails || 'No availability notes were provided yet.'}
                </p>
              </section>

              {selectedApplication.modelType === 'child' ? (
                <section className="portal-section">
                  <div className="portal-runtime-list">
                    <div className="portal-runtime-row">
                      <span>Allergies or accommodations</span>
                      <strong>{selectedApplication.allergiesOrAccommodations || 'None noted'}</strong>
                    </div>
                    <div className="portal-runtime-row">
                      <span>Participation consent</span>
                      <strong>{selectedApplication.participationConsent ? 'Confirmed' : 'Missing'}</strong>
                    </div>
                    <div className="portal-runtime-row">
                      <span>Media release</span>
                      <strong>{selectedApplication.mediaReleaseApproved ? 'Approved' : 'Not approved'}</strong>
                    </div>
                  </div>
                </section>
              ) : null}

              <section className="portal-section">
                <div className="portal-section-heading">
                  <div>
                    <p className="info-kicker">Review notes</p>
                    <h2>Set the next decision for this application.</h2>
                  </div>
                </div>

                <label className="auth-field portal-field-span-full">
                  <span>Notes for the applicant or internal team</span>
                  <textarea
                    className="auth-input auth-input--textarea"
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    rows={4}
                  />
                </label>

                <div className="portal-inline-actions">
                  {(['submitted', 'approved', 'waitlisted', 'declined'] as ModelApplicationStatus[]).map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`button-ghost tap-target ${selectedApplication.status === status ? 'portal-order-action--active' : ''}`}
                      onClick={() => void handleUpdateStatus(status)}
                      disabled={isUpdating || selectedApplication.status === status}
                    >
                      {selectedApplication.status === status ? `Current: ${formatStatusLabel(status)}` : `Mark ${formatStatusLabel(status)}`}
                    </button>
                  ))}
                </div>
              </section>
            </>
          ) : (
            <div className="auth-runtime-note">
              <p>Select a model application to review the saved profile snapshot and decision status.</p>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}
